import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Textarea } from "../../ui/textarea";
import Modal from "../../Modal";

import { getAllModels, updateModel } from "../../../utils/photogenix/utils";
import { uploadImages } from "../../../utils/utils";

import "./style.css";

export default function Models() {
  const navigate = useNavigate();
  const globalReducer = useSelector((store: any) => store.globalReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any | null>(null);
  const [selectedUrl, setSelectedUrl] = useState<string>("");

  const isDraggingRef = useRef(false);
  const draggedModelKeyRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getModels();
  }, []);

  const getModels = async () => {
    try {
      setIsLoading(true);
      const response = await getAllModels();
      setModels(response);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateModePrompt = (value: string, modelId: string) => {
    const updatedModels = models.map((model) => {
      if (model._id === modelId) {
        return { ...model, prompt: value };
      }
      return model;
    });
    setModels(updatedModels);
  };

  const handleModelDragStart = (key) => {
    isDraggingRef.current = true;
    draggedModelKeyRef.current = key;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const getModelKey = (model: Record<string, any>) =>
    String(model && (model._id ?? model.name ?? ""));

  const handleModelDrop = async (targetKey: string) => {
    try {
      const fromKey = draggedModelKeyRef.current;
      if (!fromKey || fromKey === targetKey) return;

      const base = [...models];
      const fromIndex = base.findIndex((m) => getModelKey(m) === fromKey);
      const toIndex = base.findIndex((m) => getModelKey(m) === targetKey);
      if (fromIndex === -1 || toIndex === -1) return;

      const reordered = [...base];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);

      setModels(reordered);

      const orderedIds = reordered.map((m) => m._id).filter(Boolean);
      // if (orderedIds.length) {
      //   await axiosInstance.patch("/api/options-data/ai-models", {
      //     orderedIds,
      //   });
      // }
    } catch (err) {
      console.error(err);
    } finally {
      isDraggingRef.current = false;
      draggedModelKeyRef.current = null;
    }
  };

  const handleUpdateModelData = async (dataToUpdate) => {
    try {
      setIsLoading(true);
      const response = await updateModel(dataToUpdate);
      const updatedModels = models.map((model) => {
        if (model._id === response.data._id) {
          return response.data;
        }
        return model;
      });
      setModels(updatedModels);
      if (selectedModel) {
        setSelectedModel(response.data);
      }
    } catch (err) {
      console.log("Error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateModelImage = async (imageFiles) => {
    try {
      setIsLoading(true);
      const response = await uploadImages(imageFiles);
      if (response.data.status === 200) {
        const url = response.data.data[0];
        const dataToUpdate = {
          ...selectedModel,
          image: {
            ...selectedModel.image,
            [selectedUrl]: url + "?format=webp&w=200&h=200",
          },
        };
        await handleUpdateModelData(dataToUpdate);
      } else {
        console.log(response.data);
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList;
    let validImages: File[] = [];
    Array.from(files).forEach((file) => validImages.push(file));
    updateModelImage(validImages);
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
  };

  return (
    <>
      <div>
        <div className="mode">Photogenix Model section</div>
        <div>
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <div className="flex models-container">
              {models.map((model) => (
                <div
                  key={model._id}
                  className="model-item"
                  onDragStart={() => handleModelDragStart(getModelKey(model))}
                  onDragOver={handleDragOver}
                  onDrop={() => handleModelDrop(getModelKey(model))}
                  onDragEnd={handleDragEnd}
                >
                  <div onClick={() => setSelectedModel(model)}>
                    <img src={model.image.url1} alt={model.name} />
                  </div>
                  <div>{model.name}</div>
                  <div>
                    <Textarea
                      onChange={(e: any) =>
                        updateModePrompt(e.target.value, model._id)
                      }
                      value={model.prompt}
                      rows={6}
                    />
                  </div>
                  <div
                    className="update-btn"
                    onClick={() => handleUpdateModelData(model)}
                  >
                    Update
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedModel && (
        <Modal
          onClose={() => setSelectedModel(null)}
          modalStyle={{
            width: "100%",
            height: "100%",
            right: "0",
            top: "0",
            position: "relative",
          }}
        >
          <div className="close-btn" onClick={() => setSelectedModel(null)}>
            Close
          </div>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              Loading...
            </div>
          ) : (
            <div className="model-img-list">
              {Object.keys(selectedModel?.image).map((url, idx) => (
                <div key={idx} className="model-img">
                  <img
                    src={selectedModel?.image[url]}
                    alt={selectedModel.name}
                  />
                  <div
                    className="update-btn"
                    onClick={() => {
                      setSelectedUrl(url);
                      setTimeout(() => {
                        if (fileInputRef.current) fileInputRef.current.click();
                      }, 0);
                    }}
                  >
                    Update
                  </div>
                </div>
              ))}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
